import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { getIndustrySlug, getSearchQuery, getTopicSlug } from "../../core/getParam";
import { getHomePageRoute } from "../../core/routes";
import { IIndustry, ITopic } from "../../typings/filterEntities";
import Heading from "../Heading/Heading";
import { MinusIcon, PlusIcon } from "../Icons/Icons";
import styles from "./Sidebar.module.css";

interface ISectionProps {
  isExpanded: boolean;
  label: string;
  selectedTopic: string | null;
  searchQuery: string;
  slug: string;
  topics: ITopic[];
  onToggleExpand: (s: string) => any;
}

function SidebarSection({
  isExpanded,
  label,
  slug,
  searchQuery,
  topics,
  selectedTopic,
  onToggleExpand,
}: ISectionProps) {
  return (
    <li className={styles.industry}>
      <div className={styles.industryHeading} onClick={() => onToggleExpand(slug)}>
        <Heading component="h4" variant="h5">
          {label}
        </Heading>
        <div className={styles.expandIcon}>{!isExpanded ? <PlusIcon /> : <MinusIcon />}</div>
      </div>
      <ul className={isExpanded ? styles.topicList : styles.topicListCollapsed}>
        <li key="all" className={selectedTopic === "all" ? styles.topicSelected : styles.topic}>
          <Link href={getHomePageRoute({ industry: slug, topic: "all", searchQuery })}>
            <Heading bold={false} component="h5" variant="h5">
              All Types
            </Heading>
          </Link>
        </li>
        {topics.map((t) => (
          <li
            key={t.slug}
            className={selectedTopic === t.slug ? styles.topicSelected : styles.topic}
          >
            <Link href={getHomePageRoute({ industry: slug, topic: t.slug, searchQuery })}>
              <Heading bold={false} component="h5" variant="h5">
                {t.label}
              </Heading>
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
}

interface IProps {
  hidden?: boolean;
  industries: IIndustry[];
  topics: ITopic[];
}

export default function Sidebar({ hidden, industries, topics }: IProps) {
  const router = useRouter();
  const industrySlug = getIndustrySlug(router);
  const topicSlug = getTopicSlug(router);
  const searchQuery = getSearchQuery(router);

  const [expandedSection, setExpandedSection] = React.useState<string | null>(industrySlug ?? null);

  React.useEffect(() => {
    // expand first section to show users that they are expandable
    if (industrySlug === "all" && topicSlug === "all" && !searchQuery) {
      setExpandedSection("manufacturing");
    } else if (industrySlug) {
      setExpandedSection(industrySlug);
    }
  }, [industrySlug, topicSlug, searchQuery]);

  const handleToggleExpand = React.useCallback(
    (slug: string) => {
      if (expandedSection === slug) {
        setExpandedSection(null);
      } else {
        setExpandedSection(slug);
      }
    },
    [expandedSection],
  );

  return (
    <nav className={hidden ? styles.rootHidden : styles.root}>
      <Heading component="h3" variant="h3">
        Industries &amp; Types
      </Heading>
      <ul className={styles.industryList}>
        {industries.map((i) => (
          <SidebarSection
            key={i.slug}
            slug={i.slug}
            label={i.label}
            searchQuery={searchQuery ?? ""}
            topics={topics}
            selectedTopic={i.slug === industrySlug ? topicSlug ?? "all" : null}
            isExpanded={expandedSection === i.slug}
            onToggleExpand={handleToggleExpand}
          />
        ))}
      </ul>
    </nav>
  );
}
